/**
 * ProcessingOverlay — a BLOCKING, full-screen progress surface for the long
 * server-side operations in account opening (Bill 2026-09-30, long-standing
 * request): "an overt processing spinner that stops UI interaction and
 * provides granular process feedback so the user is in the loop and does not
 * get antsy."
 *
 *   - Covers the whole app (portal to document.body), swallows clicks and
 *     keyboard focus, and cannot be dismissed by the user — the operation
 *     owns the screen until it ends or fails.
 *   - Shows a headline, a step list with live states (pending / active /
 *     done / skipped / error), the current detail line, an optional N-of-M
 *     bar, elapsed time, and a standing "keep this window open" note.
 *   - `aria-busy` + `role="dialog"` + `aria-modal` + `aria-live` so screen
 *     readers hear each step change.
 *
 * Teams-only app: there is no browser console for the FSR, so this overlay IS
 * the diagnostic surface — every step name is plain banking language.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type ProcessingStepState = 'pending' | 'active' | 'done' | 'skipped' | 'error';

export interface ProcessingStep {
  key: string;
  label: string;
  state: ProcessingStepState;
  /** Small detail under the label ("document 3 of 9 · Reg CC", an error message, a result id). */
  detail?: string;
}

export interface ProcessingOverlayProps {
  open: boolean;
  /** What is happening, in the customer's terms ("Preparing the signing session"). */
  title: string;
  /** One line under the title — the current activity. */
  message?: string;
  steps?: ReadonlyArray<ProcessingStep>;
  /** Optional determinate progress. */
  progress?: { done: number; total: number } | null;
  /** Epoch ms when the operation started — drives the elapsed clock. */
  startedAt?: number | null;
  /** Standing note; default warns not to close the window. */
  note?: string;
  /** Shown when an error state is present, e.g. a Retry button. */
  actions?: React.ReactNode;
}

function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${String(s % 60).padStart(2, '0')}s` : `${s}s`;
}

const STATE_GLYPH: Record<ProcessingStepState, string> = {
  pending: '○',
  active: '●',
  done: '✓',
  skipped: '–',
  error: '!',
};

export function ProcessingOverlay({
  open,
  title,
  message,
  steps,
  progress,
  startedAt,
  note = 'Please keep this window open. Closing or navigating away may leave the application half-processed.',
  actions,
}: ProcessingOverlayProps): JSX.Element | null {
  const [now, setNow] = useState<number>(Date.now());
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Elapsed clock — one tick per second while open.
  useEffect(() => {
    if (!open) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  // Trap focus + block the page while open (no scroll, no Tab escape).
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const prevActive = document.activeElement as HTMLElement | null;
    rootRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Tab' || e.key === 'Escape') {
        e.preventDefault();
        rootRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey, true);
      prevActive?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  const pct = progress && progress.total > 0
    ? Math.min(100, Math.round((progress.done / progress.total) * 100))
    : null;
  const hasError = steps?.some((s) => s.state === 'error') ?? false;

  return createPortal(
    <div
      ref={rootRef}
      className="proc-overlay"
      role="dialog"
      aria-modal="true"
      aria-busy={!hasError}
      aria-labelledby="proc-overlay-title"
      tabIndex={-1}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="proc-overlay__card">
        <div className="proc-overlay__head">
          {!hasError && <span className="proc-overlay__spinner" aria-hidden />}
          <div>
            <h2 id="proc-overlay-title" className="proc-overlay__title">{title}</h2>
            {message && <p className="proc-overlay__message" aria-live="polite">{message}</p>}
          </div>
          {startedAt ? (
            <span className="proc-overlay__elapsed" aria-label="elapsed time">{fmtElapsed(now - startedAt)}</span>
          ) : null}
        </div>

        {pct !== null && (
          <div className="proc-overlay__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
            <div className="proc-overlay__bar-fill" style={{ width: `${pct}%` }} />
          </div>
        )}

        {steps && steps.length > 0 && (
          <ol className="proc-overlay__steps" aria-live="polite">
            {steps.map((s) => (
              <li key={s.key} className={`proc-overlay__step is-${s.state}`}>
                <span className="proc-overlay__glyph" aria-hidden>{STATE_GLYPH[s.state]}</span>
                <span className="proc-overlay__label">{s.label}</span>
                {s.detail && <span className="proc-overlay__detail">{s.detail}</span>}
              </li>
            ))}
          </ol>
        )}

        {actions && <div className="proc-overlay__actions">{actions}</div>}
        <p className="proc-overlay__note">{note}</p>
      </div>
    </div>,
    document.body,
  );
}

export default ProcessingOverlay;
